import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { DraggableModalWrapperComponent } from '../../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { AttachmentOperationModalData } from './models/attachment-operation-modal-data';
import { NestedFormGroupType } from '../../../../../../../core/models/form-group-type';
import { AttachmentFormModel } from '../../../models/attachment/attachment-form-model';
import { AttachmentOperationFormService } from './services/attachment-operation-form/attachment-operation-form.service';
import { AttachmentFormService } from './services/attachment-form/attachment-form.service';
import { AttachmentOperationFormModel } from '../../../models/attachment/attachment-operation-form-model';
import { AttachmentUploadComponent } from '../attachment-upload/attachment-upload.component';
import { AttachmentListComponent } from '../attachment-list/attachment-list.component';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import { AttachmentEventService } from '../../../services/attachment-event/attachment-event.service';
import { forkJoin, Observable, tap } from 'rxjs';
import { ApplicationService } from '../../../../../shared/services/application/application.service';

@Component({
    selector: 'app-attachment-operation',
    templateUrl: './attachment-operation.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatDialogContent,
        MatSelectModule,
        MatDialogActions,
        MatDialogClose,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        DraggableModalWrapperComponent,
        AttachmentListComponent,
        AttachmentUploadComponent,
    ],
})
export class AttachmentOperationComponent {
    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: AttachmentOperationModalData,
        private readonly applicationService: ApplicationService,
        private readonly attachmentService: AttachmentService,
        private readonly attachmentEventService: AttachmentEventService,
        private readonly attachmentFormService: AttachmentFormService,
        private readonly attachmentOperationFormService: AttachmentOperationFormService,
    ) {}

    form: FormGroup<NestedFormGroupType<AttachmentOperationFormModel>> =
        this.attachmentOperationFormService.getFormDefinition();

    /**
     * Checks to see if ALL attachments in the form array are valid with an attachment type code.
     * Any attachments marked for deletion are ignored in this check.
     *
     * @returns true if all attachments (that are not marked for deletion) are valid, false otherwise.
     */
    canSave(): boolean {
        const attachments = this.attachmentOperationFormService.getAttachments(
            this.form,
        );

        return attachments.controls.every(
            (
                attachmentForm: FormGroup<
                    NestedFormGroupType<AttachmentFormModel>
                >,
            ) => {
                if (
                    this.attachmentFormService.isDeletedAttachment(
                        attachmentForm,
                    )
                ) {
                    return true;
                }

                return attachmentForm.valid;
            },
        );
    }

    /**
     * Handles the save operation for the attachments.
     * It processes the attachments in the form and performs create, update, or delete operations
     * as necessary based on their state.
     */
    onSave(): void {
        if (!this.canSave()) {
            return;
        }

        const operations = this.buildAttachmentOperations();
        if (operations.length === 0) {
            return;
        }

        forkJoin(operations)
            .pipe(
                tap(() => {
                    this.attachmentEventService.notifyApplicationAttachmentsChanged();
                }),
                // Ensure application state reflects any new changes due to attachment operations.
                tap(() => {
                    this.applicationService.refreshApplicationStateAsync(
                        this.data.applicationId,
                    );
                }),
            )
            .subscribe();
    }

    /**
     * Builds an array of observables representing the operations (create, update, delete)
     * to be performed on the attachments based on their state in the form.
     *
     * New attachments will generate create operations, existing attachments with changes
     * will generate update operations, and deleted attachments will generate delete operations.
     *
     * @returns An array of observables for the attachment operations to be performed.
     */
    private buildAttachmentOperations(): Observable<void>[] {
        const attachments = this.attachmentOperationFormService.getAttachments(
            this.form,
        );

        return attachments.controls
            .map((attachment) => this.createOperationForAttachment(attachment))
            .filter((operation) => operation !== null) as Observable<void>[];
    }

    /**
     * Creates the appropriate operation (create, update, delete) for a given attachment form.
     *
     * @param attachment The attachment form group to evaluate.
     * @returns An observable representing the operation to be performed, or null if no operation is needed.
     */
    private createOperationForAttachment(
        attachment: FormGroup<NestedFormGroupType<AttachmentFormModel>>,
    ): Observable<void> | null {
        const applicationId = this.data.applicationId;

        if (this.attachmentFormService.isDeletedAttachment(attachment)) {
            return this.attachmentService.delete(
                applicationId,
                attachment.controls.id.value,
            );
        }

        if (this.attachmentFormService.isNewAttachment(attachment)) {
            const request =
                this.attachmentFormService.generateCreationRequestObject(
                    attachment,
                );
            return this.attachmentService.create(applicationId, request);
        }

        const request =
            this.attachmentFormService.generateUpdateRequestObject(attachment);
        return this.attachmentService.update(
            applicationId,
            attachment.controls.id.value,
            request,
        );
    }
}
